function cartDrawerSectionId() {
  const drawer = document.querySelector('cart-drawer');
  const wrapper = drawer && drawer.closest('.shopify-section');
  if (wrapper && wrapper.id.indexOf('shopify-section-') === 0) {
    return wrapper.id.slice('shopify-section-'.length);
  }
  return 'cart-drawer';
}
window.cartDrawerSectionId = cartDrawerSectionId;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }
  connectedCallback(){
    if(window.location.hash === '#cart-open'){
      this.open();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, shouldOpen = true) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // Only pop the drawer open in response to a genuine user action (add-to-cart,
      // quantity change, etc). Background reconciliation (gift auto-add/remove on
      // connectedCallback, which also fires on every plain page load) must never
      // force the drawer open on its own - see ProgressBar.updateCartDrawer().
      if (shouldOpen) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: cartDrawerSectionId(),
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: cartDrawerSectionId(),
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class ProgressBar extends HTMLElement {
  constructor() {
    super();
    this.thresholdsMet = new Set();
  }

  connectedCallback() {
    this.checkThresholds();
  }

  async checkThresholds() {
    this.setCheckoutButtonDisabled(true);

    const total = parseFloat(this.dataset.total) || 0;
    const minSpend1 = parseFloat(this.dataset.minSpend_1) || 0;
    const minSpend2 = parseFloat(this.dataset.minSpend_2) || 0;
    const minSpend3 = parseFloat(this.dataset.minSpend_3) || 0;
    let currentThreshold = 0;
    if (total >= minSpend3) currentThreshold = 3;
    else if (total >= minSpend2) currentThreshold = 2;
    else if (total >= minSpend1) currentThreshold = 1;
    else currentThreshold = 0;
    if (currentThreshold >= 1 && !this.thresholdsMet.has('threshold1')) {
      await this.addGiftProducts('giftsTier_1');
      this.thresholdsMet.add('threshold1');
    }
    if (currentThreshold >= 2 && !this.thresholdsMet.has('threshold2')) {
      await this.addGiftProducts('giftsTier_2');
      this.thresholdsMet.add('threshold2');
    }
    if (currentThreshold >= 3 && !this.thresholdsMet.has('threshold3')) {
      await this.addGiftProducts('giftsTier_3');
      this.thresholdsMet.add('threshold3');
    }
    for (const tier of [3, 2, 1]) {
      if (currentThreshold >= tier) continue;
      const upsellsToRemove = [
        this.dataset[`giftsTier_${tier}`],
        this.dataset[`manualGiftsTier_${tier}`]
      ]
        .filter(list => list && list.trim() !== '')
        .join(',');
      if (upsellsToRemove.trim() !== '') {
        await this.removeUpsellProducts(upsellsToRemove);
      }
    }

    await this.enforceGiftQuantityLimit();
    this.setCheckoutButtonDisabled(false);
  }

  async removeUpsellProducts(upsellsToRemove) {
    if (!upsellsToRemove || upsellsToRemove.trim() === '') {
      return;
    }
    const productIds = upsellsToRemove.split(',').map(id => id.trim()).filter(id => id);
    if (productIds.length === 0) {
      return;
    }
    try {
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      const itemsToRemove = cartData.items.filter(item => {
        // Only ever auto-remove lines this code itself auto-added. A shopper who
        // deliberately bought the same product (e.g. the T-shirt or the add-on
        // buttons) must keep it even if the cart total dips back below a tier.
        const isAutoAddedGift = !!(item.properties && (item.properties._auto_gift === 'true' || item.properties._gift_claimed === 'true'));
        if (!isAutoAddedGift) {
          return false;
        }
        if (item.variant_id && productIds.includes(item.variant_id.toString())) {
          return true;
        }
        if (item.id && productIds.includes(item.id.toString())) {
          return true;
        }
        if (item.product_id && productIds.includes(item.product_id.toString())) {
          return true;
        }
        return false;
      });
      if (itemsToRemove.length === 0) {
        return;
      }
      const updates = {};
      itemsToRemove.forEach(item => {
        updates[item.key] = 0;
      });
      const body = JSON.stringify({ updates });
      const cartUpdateUrl = window.routes?.cart_update_url || '/cart/update.js';
      const response = await fetch(cartUpdateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body
      });

      if (response.ok) {
        const cart = await response.json();
        await this.updateCartDrawer();
      } else {
        const errorText = await response.text();
        console.error('Error removing gift products:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error removing gift products:', error);
    }
  }

  async addGiftProducts(giftKey) {
    const giftIds = this.dataset[giftKey];
    if (!giftIds || giftIds.trim() === '') {
      return;
    }
    const productIds = giftIds.split(',').map(id => id.trim()).filter(id => id);
    if (productIds.length === 0) {
      return;
    }
    try {
      const cartItems = document.querySelectorAll('[data-variant-id]');
      const existingProductIds = Array.from(cartItems).map(item => item.getAttribute('data-variant-id'));
      const productsToAdd = productIds.filter(productId => !existingProductIds.includes(productId));
      if (productsToAdd.length === 0) {
        return;
      }
      for (const productId of productsToAdd) {
        await this.addToCart(productId, 1);
      }
      await this.updateCartDrawer();
    } catch (error) {
      console.error(`Error adding gift products for ${giftKey}:`, error);
    }
  }

  async enforceGiftQuantityLimit() {
    try {
      const tiers = [
        this.dataset.giftsTier_1 || '',
        this.dataset.giftsTier_2 || '',
        this.dataset.giftsTier_3 || '',
        this.dataset.manualGiftsTier_1 || '',
        this.dataset.manualGiftsTier_2 || '',
        this.dataset.manualGiftsTier_3 || ''
      ]
        .filter(Boolean)
        .join(',');

      if (!tiers || tiers.trim() === '') {
        return;
      }

      let giftVariantIds = tiers
        .split(',')
        .map(id => id.trim())
        .filter(id => id);

      const domGiftVariantIds = Array.from(document.querySelectorAll('[data-gift-item="true"][data-variant-id]'))
        .map(node => node.getAttribute('data-variant-id'))
        .filter(id => id);
      if (domGiftVariantIds.length > 0) {
        giftVariantIds = Array.from(new Set([...giftVariantIds, ...domGiftVariantIds]));
      }

      if (giftVariantIds.length === 0) {
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      const updates = {};
      for (const item of cartData.items) {
        const variantIdStr = item.variant_id ? item.variant_id.toString() : '';
        const isAutoAddedGift = !!(item.properties && (item.properties._auto_gift === 'true' || item.properties._gift_claimed === 'true'));
        // Same rule as removeUpsellProducts: never clamp quantity on a line the
        // shopper added themselves, even if its variant matches a gift tier.
        if (variantIdStr && giftVariantIds.includes(variantIdStr) && item.quantity > 1 && isAutoAddedGift) {
          updates[item.key] = 1;
        }
      }

      if (Object.keys(updates).length === 0) {
        return;
      }

      const body = JSON.stringify({ updates });
      const cartUpdateUrl = window.routes?.cart_update_url || '/cart/update.js';
      const response = await fetch(cartUpdateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body
      });

      if (response.ok) {
        await response.json();
        await this.updateCartDrawer();
      } else {
        const errorText = await response.text();
        console.error('Error enforcing gift quantity limit:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error enforcing gift quantity limit:', error);
    }
  }

  async addToCart(productId, quantity) {
    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: parseInt(productId, 10),
          quantity: quantity,
          // Underscore-prefixed properties are hidden from the customer and from
          // checkout display (see cart-drawer.liquid's `first_char != '_'` checks).
          // This tags the line as auto-added so removeUpsellProducts /
          // enforceGiftQuantityLimit never touch a shopper's own purchase of the
          // same product.
          properties: { '_auto_gift': 'true' }
        }]
      })
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.description || 'Error adding item to cart');
    }
    return response.json();
  }

  async updateCartDrawer(shouldOpen = false) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart?sections=${cartDrawerSectionId()},cart-icon-bubble`
      );
      const sectionsData = await response.json();
      // Default is NOT to open: this is called from checkThresholds' background
      // gift reconciliation, which also runs on every plain page load
      // (ProgressBar.connectedCallback). Opening here would pop the drawer with
      // no user action the first time a page load happens to cross a threshold.
      cartDrawer.renderContents({ sections: sectionsData }, shouldOpen);
    } catch (error) {
      console.error('Error updating cart drawer:', error);
    }
  }

  setCheckoutButtonDisabled(disabled) {
    const checkoutButton = document.querySelector('#CartDrawer-Checkout');
    if (!checkoutButton) return;
    if (!disabled && checkoutButton.getAttribute('data-gift-in-cart') === 'false') return;
    checkoutButton.disabled = disabled;
    if (disabled) {
      checkoutButton.setAttribute('aria-disabled', 'true');
      checkoutButton.classList.add('disabled');
    } else {
      checkoutButton.removeAttribute('aria-disabled');
      checkoutButton.classList.remove('disabled');
    }
  }
}

customElements.define('progress-bar', ProgressBar);
class GiftSelect extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (evt) => {
      evt.target.closest('.gift-select-wrapper').querySelector('upsell-add-to-cart-button').setAttribute('data-product-id', evt.target.querySelector('option:checked').value);
    });
  }
}

customElements.define('gift-select', GiftSelect);